import React, { useState, useMemo, useEffect } from "react";
import {
  Typography,
  Button,
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Divider,
  Paper,
  List,
  ListItem,
  Avatar,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Stack,
  Tooltip,
  Collapse,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { createRazorpayOrder, getRazorpayKey } from "./api/payment";
import { useNavigate } from "react-router-dom";
import { IMG_DAINASOUL_LOGO_NEW as DainasoulLogo } from "./imageConfig";
import CheckoutPaymentSection from "./CheckoutPaymentSection";
import axios from "axios";
import { useAuth } from "./contexts/AuthContext";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GooglePayButton from '@google-pay/button-react';
import GooglePaySvg from "./images/GooglePaySvg.js";
import PaypalIcon from "./images/PaypalIcon.js";
import { PayPalButtons } from "@paypal/react-paypal-js";
import CustomAddressForm from "./CustomAddressForm.js";
import { useCheckout } from "./CheckoutContext.js";

const SHIPPING_RATES = {
  standard: 0.0,
  express: 250.0,
};

// Helper component for Order Summary Content
const OrderSummaryContent = ({
  cartItems,
  formatPrice,
  userCountry,
  couponInput,
  setCouponInput,
  couponError,
  setCouponError,
  handleApplyCoupon,
  isApplyingCoupon,
  subtotal,
  shippingCost,
  appliedCoupon,
  discountAmount,
  grandTotal,
  isDesktop
}) => (
  <Box sx={{ maxWidth: isDesktop ? "450px" : "100%", mx: "auto" }}>
    <List disablePadding>
      {cartItems.map((item, index) => {
        const priceStr = String(item.price).replace(/[^0-9.-]+/g, "");
        const price = parseFloat(priceStr) || 0;
        const total = price * (item.quantity || 1);

        return (
          <ListItem
            key={item.id || index}
            sx={{
              px: 0,
              py: 2,
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ position: "relative", mr: 2 }}>
              <Avatar
                src={item.image}
                variant="square"
                sx={{ width: 60, height: 70 }}
              />
              {/* Quantity badge */}
              <Box
                sx={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  backgroundColor: "#444444d1",
                  color: "#fff",
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  fontSize: "12px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {item.quantity}
              </Box>
            </Box>

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: 1.3,
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                {item.name || item.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "12px",
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                {item.size ? item.size.toUpperCase() : ""}
              </Typography>
            </Box>

            <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
              {formatPrice(total, userCountry?.code || "IN")}
            </Typography>
          </ListItem>
        );
      })}
    </List>

    <Box sx={{ mt: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          placeholder="Discount code or gift card"
          value={couponInput}
          onChange={(e) => {
            setCouponInput(e.target.value);
            if (couponError) setCouponError("");
          }}
          error={!!couponError}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#fff',
              borderRadius: '8px',
              height: '52px',
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#000',
                borderWidth: '1px',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#999',
              },
              '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                borderColor: '#d32f2f',
              }
            }
          }}
        />
        <Button
          variant="contained"
          onClick={handleApplyCoupon}
          disabled={!couponInput || isApplyingCoupon}
          sx={{
            backgroundColor: '#333',
            color: '#fff',
            textTransform: 'none',
            width: '90px',
            height: '52px',
            boxShadow: 'none',
            borderRadius: '8px',
            position: 'relative',
            fontSize: '15px',
            border: '1px solid transparent',
            '&:hover': {
              backgroundColor: '#000',
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              backgroundColor: isApplyingCoupon ? '#333' : '#f5f5f5',
              color: isApplyingCoupon ? '#fff' : '#717171',
              border: isApplyingCoupon ? '1px solid #333' : '1px solid #e1e1e1',
            }
          }}
        >
          {isApplyingCoupon ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Apply"
          )}
        </Button>
      </Box>
      {couponError && (
        <Typography
          sx={{
            mt: 1,
            fontSize: '12px',
            color: '#d32f2f',
            fontWeight: 500
          }}
        >
          Enter a valid discount code or gift card
        </Typography>
      )}
    </Box>

    <Box sx={{ mb: 2 }}>
      <Box
        sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
      >
        <Typography sx={{ fontSize: { xs: "14px", sm: "16px" } }}>
          Subtotal
        </Typography>
        <Typography sx={{ fontSize: { xs: "14px", sm: "16px" } }}>
          {formatPrice(subtotal, userCountry?.code || "IN")}
        </Typography>
      </Box>
      <Box
        sx={{ display: "flex", justifyContent: "space-between", mb: 1, alignItems: "center" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontSize: "14px", color: "#555" }}>
            Shipping
          </Typography>
          <Tooltip title="Shipping costs are calculated based on your location.">
            <HelpOutlineIcon sx={{ fontSize: "14px", color: "#999", cursor: "pointer" }} />
          </Tooltip>
        </Box>
        <Typography sx={{ fontSize: "14px", color: "#333", fontWeight: 400 }}>
          {!userCountry?.code
            ? "Enter shipping address"
            : "FREE"}
        </Typography>
      </Box>
      {discountAmount > 0 && (
        <Box
          sx={{ display: "flex", justifyContent: "space-between", mb: 1, color: "green" }}
        >
          <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
            Discount ({appliedCoupon})
          </Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
            -{formatPrice(discountAmount, userCountry?.code || "IN")}
          </Typography>
        </Box>
      )}
    </Box>

    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3, alignItems: "flex-start" }}>
      <Box>
        <Typography
          variant="h6"
          sx={{ fontWeight: 400, fontSize: "18px", color: "#000" }}
        >
          Total
        </Typography>
        {/* <Typography variant="caption" sx={{ color: "#777", display: "block", mt: 0.5, textAlign: "right" }}>
          Including {formatPrice(grandTotal * 0.18, userCountry?.code || "IN")} in taxes
        </Typography> */}
      </Box>
      <Box sx={{ textAlign: "right" }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 400, fontSize: "20px", color: "#000", display: "flex", alignItems: "baseline", gap: 1 }}
        >
          <span style={{ fontSize: "12px", color: "#999", fontWeight: "normal" }}>
            {userCountry?.code === "IN" ? "INR" : "USD"}
          </span>
          {formatPrice(grandTotal, userCountry?.code || "IN")}
        </Typography>
      </Box>
    </Box>
  </Box>
);

const CheckoutForm = ({
  cartItems = [],
  shippingDetails = {},
  subtotal = 0,
  tax = 0,
  handleShippingChange,
  shippingErrors,
  setShippingErrors,
  userCountry,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [shippingMethod] = useState("standard");
  const [saveInfo, setSaveInfo] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [showTermsError, setShowTermsError] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const navigate = useNavigate();
  const { state: checkoutState, dispatch } = useCheckout();
  const { appliedCoupon } = checkoutState;

  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [isReviewLoading, setIsReviewLoading] = useState(false);
  // const [razorpayKey, setRazorpayKey] = useState(null);
  // const [razorpayOrder, setRazorpayOrder] = useState(null);
  const { user, logout } = useAuth();

  const [paymentMethod, setPaymentMethod] = useState("");
  const [useBillingAddress, setUseBillingAddress] = useState("same");
  const [cardDetails, setCardDetails] = useState(true);

  const [exchangeRate, setExchangeRate] = useState(82); // Default: 1 USD = 82 INR
  const [couponInput, setCouponInput] = useState("");
  const [couponError, setCouponError] = useState("");
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);

  const BACKEND_URL =
    (process.env.NODE_ENV === "development" && "http://localhost:8000") || "";

  // Fetch live exchange rate (run once on component mount)
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const response = await fetch(
          "https://api.exchangerate-api.com/v4/latest/USD"
        );
        const data = await response.json();
        setExchangeRate(data.rates.INR); // Get current INR rate
      } catch (error) {
        console.error(
          "Failed to fetch exchange rate, using default 82:",
          error
        );
      }
    };
    fetchExchangeRate();
  }, []);

  const REQUIRED_FIELDS = [
    "email",
    "firstName",
    "lastName",
    "address1",
    "city",
    "zipCode",
    "phone",
    "country",
  ];

  useEffect(() => {
    if (shippingDetails?.country) {
      if (shippingDetails.country.toLowerCase() === "india") {
        setPaymentMethod("Razorpay");
      } else {
        setPaymentMethod("Paypal");
      }
    }
  }, [shippingDetails?.country]);

  // Add these handler functions
  const handlePaymentChange = (e) => {
    setPaymentMethod(e.target.value);
  };

  const handleBillingChange = (value) => {
    setUseBillingAddress(value);
  };

  const handleCardDetailsChange = (event) => {
    setCardDetails({
      ...cardDetails,
      cardNumber: event.target.value,
    });
  };

  // Hide footer when component mounts
  useEffect(() => {
    const footer = document.querySelector("footer");
    if (footer) {
      footer.style.display = "none";
    }

    return () => {
      if (footer) {
        footer.style.display = "block";
      }
    };
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  const shippingCost = useMemo(() => {
    if (shippingMethod === "standard" && subtotal > 100) {
      return 0;
    }
    return SHIPPING_RATES[shippingMethod];
  }, [shippingMethod, subtotal]);

  const discountAmount = useMemo(() => {
    if (appliedCoupon === "WELCOME10") {
      return subtotal * 0.1;
    }
    return 0;
  }, [appliedCoupon, subtotal]);

  const grandTotal = useMemo(
    () => subtotal + shippingCost - discountAmount,
    [subtotal, shippingCost, discountAmount]
  );

  const handleReviewOrder = (e) => {
    e.preventDefault();

    const newErrors = {};
    REQUIRED_FIELDS.forEach((field) => {
      if (!shippingDetails[field] || shippingDetails[field].trim() === "") {
        newErrors[field] = "This field is required";
      }
    });

    if (!termsAccepted) {
      setShowTermsError(true);
    } else {
      setShowTermsError(false);
    }

    if (Object.keys(newErrors).length > 0 || !termsAccepted) {
      setShippingErrors(newErrors);
      return;
    }

    // No errors, proceed
    setShippingErrors({});
    setIsReviewLoading(true);

    setTimeout(() => {
      setIsReviewLoading(false);
      setShowReview(true);
    }, 1500);
  };

  const checkoutHandler = async () => {
    setIsPaymentLoading(true); // show spinner

    try {
      // 1. Get Razorpay key
      const key = await getRazorpayKey();
      // setRazorpayKey(key);

      // 2. Create Razorpay order
      const { order } = await createRazorpayOrder(Math.round(grandTotal * 100));
      // setRazorpayOrder(order);

      // 3. Proceed to payment
      const options = {
        key: key,
        amount: order.amount,
        currency: "INR",
        name: "DAINASOUL",
        description: `Payment for ${cartItems.length
          } items (₹${grandTotal.toFixed(2)})`,
        image: DainasoulLogo,
        order_id: order.id,
        handler: async function (response) {
          try {
            const verificationResponse = await axios.post(
              `${BACKEND_URL}/api/v1/paymentVerification`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }
            );
            if (verificationResponse.data.razorpay_payment_id) {
              // ✅ Save order to context
              const orderData = {
                id: order.id,
                paymentId: response.razorpay_payment_id,
                date: new Date().toISOString(),
                items: cartItems,
                total: grandTotal,
                shippingDetails,
                status: "Paid",
              };

              dispatch({ type: "ADD_ORDER", payload: orderData });

              // ✅ Send the Welcome & Discount Email
              try {
                await fetch(`${BACKEND_URL}/api/email/welcome`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ email: shippingDetails.email }),
                });
              } catch (emailErr) {
                console.error("Failed to send welcome email:", emailErr);
              }

              // ✅ Clear cart after successful order
              dispatch({ type: "CLEAR_CART" });
              // ✅ Manual redirect after successful payment
              window.location.href = `/paymentSuccessful?reference=${response.razorpay_payment_id}`;
            } else {
              alert("Payment failed verification");
            }
          } catch (err) {
            console.error("Verification failed", err);
            alert("Payment verification error");
          }
        },
        prefill: {
          name: `${shippingDetails.firstName || ""} ${shippingDetails.lastName || ""
            }`,
          email: shippingDetails.email || "",
          contact: shippingDetails.phone || "",
        },
        notes: {
          address: `${shippingDetails.address1 || ""} ${shippingDetails.address2 || ""
            }`,
          cartItems: JSON.stringify(cartItems),
        },
        theme: {
          color: "#000000",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (response) {
        console.error("Payment failed:", response.error);
      });

      rzp.open();
    } catch (err) {
      console.error("Error during Razorpay setup:", err);
      alert("Failed to initialize payment. Please try again.");
    } finally {
      setIsPaymentLoading(false); // stop spinner
    }
  };

  const formatPrice = (amount, countryCode) => {
    let locale = "en-US";
    let currency = "USD";
    const code = countryCode?.toUpperCase();

    switch (code) {
      case "IN":
        locale = "en-IN";
        currency = "INR";
        break;
      case "CA":
        locale = "en-CA";
        currency = "CAD";
        break;
      case "GB":
      case "UK":
        locale = "en-GB";
        currency = "GBP";
        break;
      case "FR":
      case "DE":
      case "IT":
      case "ES":
      case "NL":
      case "BE":
      case "AT":
      case "PT":
      case "IE":
      case "GR":
        locale = "en-IE"; // Using English-Ireland for generic Euro format
        currency = "EUR";
        break;
      default:
        locale = "en-US";
        currency = "USD";
    }

    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const handleApplyCoupon = () => {
    setIsApplyingCoupon(true);
    const normalizedCoupon = couponInput.toUpperCase();

    // Simulate a small network delay for a more professional feel
    setTimeout(() => {
      if (normalizedCoupon === "WELCOME10") {
        dispatch({ type: "SET_COUPON", payload: "WELCOME10" });
        setCouponError("");
      } else {
        setCouponError(`${normalizedCoupon} discount code isn't valid for the items in your cart`);
      }
      setIsApplyingCoupon(false);
    }, 800);
  };

  const getActiveCountryCode = (countryName) => {
    if (!countryName) return userCountry?.code || "IN";
    const name = countryName.toLowerCase();
    if (name === "india") return "IN";
    if (name === "canada") return "CA";
    if (name === "united kingdom") return "GB";
    if (["france", "germany", "italy", "spain", "netherlands", "belgium", "austria", "portugal", "ireland", "greece"].includes(name)) return "FR"; // Or any EU code
    if (name === "united states") return "US";
    return userCountry?.code || "US";
  };

  const activeCountryCode = getActiveCountryCode(shippingDetails.country);

  return (
    <Box
      sx={{
        display: "flex",
        maxWidth: { xs: "100%", lg: 1200 },
        mx: "auto",
        mt: { xs: 0, sm: 0, md: 0 },
        gap: { xs: 2, sm: 3, md: 4 },
        height: { xs: "auto", lg: "calc(100vh - 64px)" },
        overflow: "hidden",
        px: { xs: 0, lg: 4 },
        flexDirection: { xs: "column", lg: "row" },
        overflow: "hidden",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* 🟢 Mobile/Tablet Order Summary Toggle Bar */}
      {!isDesktop && (
        <Box
          sx={{
            order: { xs: 0, lg: 2 },
            width: "100%",
            mx: 0,
            borderTop: "1px solid #e1e1e1",
            borderBottom: "1px solid #e1e1e1",
            backgroundColor: "#fafafa",
            zIndex: 1100,
          }}
        >
          <Box
            onClick={() => setSummaryExpanded(!summaryExpanded)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: "18px 20px",
              cursor: "pointer",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
              <Typography sx={{ fontSize: "14px", color: "#000" }}>
                {summaryExpanded ? "Order summary" : "Order summary"}
              </Typography>
              <ExpandMoreIcon
                sx={{
                  fontSize: "18px",
                  transition: "transform 0.3s",
                  transform: summaryExpanded ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </Box>
            <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
              {formatPrice(grandTotal, userCountry?.code || "IN")}
            </Typography>
          </Box>
          <Collapse in={summaryExpanded}>
            <Box sx={{ p: "0 20px 20px" }}>
              {/* Reuse Desktop Summary Content but without the side padding/borders */}
              <OrderSummaryContent
                cartItems={cartItems}
                formatPrice={formatPrice}
                userCountry={{ code: activeCountryCode }}
                couponInput={couponInput}
                setCouponInput={setCouponInput}
                couponError={couponError}
                setCouponError={setCouponError}
                handleApplyCoupon={handleApplyCoupon}
                isApplyingCoupon={isApplyingCoupon}
                subtotal={subtotal}
                shippingCost={shippingCost}
                appliedCoupon={appliedCoupon}
                discountAmount={discountAmount}
                grandTotal={grandTotal}
                isDesktop={false}
              />
            </Box>
          </Collapse>
        </Box>
      )}
      {/* Left Side - Form (Scrollable) */}
      {showReview ? (
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: 600 },
            mx: "auto",
            mt: { xs: 3, sm: 5, lg: 8 },
            px: { xs: 2, sm: 4, lg: 0 },
            pb: 4,
            boxSizing: "border-box",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 3 }}>
            Complete your order
          </Typography>

          {/* Contact Information */}
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontSize: "14px", color: "gray" }}
              >
                Email
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  textDecoration: "underline",
                  color: "black",
                  cursor: "pointer",
                }}
                onClick={() => setShowReview(false)}
              >
                Change
              </Typography>
            </Box>

            <Typography sx={{ fontSize: "14px", mt: 0.5, wordBreak: "break-word" }}>
              {user?.email || shippingDetails.email || "No email provided"}
            </Typography>
          </Box>

          <Divider />

          {/* Shipping Address */}
          <Box sx={{ mb: 3, mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontSize: "14px", color: "gray" }}
              >
                Ship to
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  textDecoration: "underline",
                  color: "black",
                  cursor: "pointer",
                }}
                onClick={() => setShowReview(false)}
              >
                Change
              </Typography>
            </Box>

            <Typography sx={{ fontSize: "14px", mt: 0.5, wordBreak: "break-word" }}>
              {shippingDetails.firstName} {shippingDetails.lastName},{" "}
              {shippingDetails.address1},{" "}
              {shippingDetails.address2 ? `${shippingDetails.address2}, ` : ""}
              {shippingDetails.city}, {shippingDetails.zipCode},{" "}
              {shippingDetails.country}
              <br />
              {shippingDetails.phone}
            </Typography>
          </Box>

          <Divider sx={{ my: 0 }} />

          {/* Shipping Method */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 1, fontSize: "14px", color: "gray" }}
            >
              Shipping method
            </Typography>
            <Typography sx={{ fontSize: "14px", mt: 0.5 }}>
              Standard Shipping · Free
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "gray" }}>
              No other methods available
            </Typography>
          </Box>

          <Divider sx={{ my: 0 }} />

          {/* Payment Method */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontSize: "14px", color: "gray" }}
              >
                Payment method
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  textDecoration: "underline",
                  color: "black",
                  cursor: "pointer",
                }}
                onClick={() => setShowReview(false)}
              >
                Change
              </Typography>
            </Box>

            <Typography
              sx={{ fontSize: "14px", mt: 0.5, wordBreak: "break-word" }}
            >
              {paymentMethod === "Razorpay" &&
              shippingDetails.country === "India"
                ? "Razorpay Secure"
                : "PayPal"}
              <br />
              {shippingDetails.firstName} {shippingDetails.lastName},{" "}
              {shippingDetails.address1},{" "}
              {shippingDetails.address2 ? `${shippingDetails.address2}, ` : ""}
              {shippingDetails.city}, {shippingDetails.zipCode},{" "}
              {shippingDetails.country}
              <br />
              {shippingDetails.phone}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Pay Now Button */}
          <Button
            variant="contained"
            fullWidth
            disableRipple
            disableElevation
            disabled={isPaymentLoading}
            sx={{
              backgroundColor: "black",
              color: "white",
              py: 1.5,
              fontSize: "16px",
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: "bold",
              minHeight: "48px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              "&:hover": {
                backgroundColor: "black",
              },
              "&:focus": {
                backgroundColor: "black",
              },
              "&:active": {
                backgroundColor: "black",
              },
              "&.Mui-disabled": {
                backgroundColor: "black",
                color: "white",
                opacity: 1,
              },
            }}
            onClick={checkoutHandler}
          >
            {isPaymentLoading ? (
              <CircularProgress
                size={18}
                sx={{ color: "white", position: "absolute" }}
              />
            ) : null}

            {/* Always render text to preserve layout */}
            <span
              style={{ visibility: isPaymentLoading ? "hidden" : "visible" }}
            >
              Pay now
            </span>
          </Button>
        </Box>
      ) : (
        <Box
          component="form"
          onSubmit={handleReviewOrder}
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            mt: { xs: 3, sm: 4, lg: 7 },
            maxWidth: { xs: "100%", sm: 600 },
            mx: "auto",
            px: { xs: 2.5, sm: 4, lg: 0 },
            pb: 4,
            mr: { xs: "auto", lg: 2 },
            boxSizing: "border-box",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: "14px",
                mb: 2,
                textAlign: "center",
                color: "gray",
              }}
            >
              Express checkout
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mb: 2 }}
            >

              {/* PayPal */}
              <Box
                sx={{
                  flex: 1,
                  width: { xs: "100%", sm: "auto" },
                  height: "52px",
                  minHeight: "52px",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    iframe: {
                      borderRadius: "8px !important",
                      height: "52px !important",
                    },
                  }}
                >
                  <PayPalButtons
                    style={{
                      layout: "horizontal",
                      color: "gold",
                      shape: "rect",
                      height: 52,
                      tagline: false,
                    }}
                    disabled={shippingDetails.country === "India"}
                    createOrder={(data, actions) => {
                      // Convert INR to USD if customer is not from India
                      const amount = (grandTotal / exchangeRate).toFixed(2); // Convert INR to USD (1 USD ≈ 82 INR)
                      return actions.order.create({
                        purchase_units: [
                          {
                            amount: {
                              value: amount, // dynamically use cart total
                            },
                          },
                        ],
                      });
                    }}
                    onApprove={(data, actions) => {
                      return actions.order.capture().then((details) => {
                        alert(
                          `Transaction completed by ${details.payer.name.given_name}`
                        );
                      });
                    }}
                    onError={(err) => {
                      console.error("PayPal Checkout Error:", err);
                    }}
                  />
                </Box>
              </Box>

              {/* Google Pay */}
              {/* <Box
                sx={{
                  flex: 1,
                  width: { xs: "100%", sm: "auto" },
                  height: "52px",
                  minHeight: "52px",
                  borderRadius: "8px",
                  overflow: "hidden",
                  "& > div": { height: "100%", width: "100%" },
                  "& button": { height: "52px", borderRadius: "8px", width: "100%" }
                }}
              >
                <GooglePayButton
                  environment="PRODUCTION"
                  buttonSizeMode="fill"
                  buttonType="plain"
                  buttonColor="black"
                  paymentRequest={{
                    apiVersion: 2,
                    apiVersionMinor: 0,
                    allowedPaymentMethods: [
                      {
                        type: 'CARD',
                        parameters: {
                          allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                          allowedCardNetworks: ['MASTERCARD', 'VISA'],
                        },
                        tokenizationSpecification: {
                          type: 'PAYMENT_GATEWAY',
                          parameters: {
                            gateway: 'razorpay',
                            gatewayMerchantId: 'rzp_live_a0c8PGCkaCAmmy',
                          },
                        },
                      },
                    ],
                    merchantInfo: {
                      merchantId: 'BCR2DN4TTXY6ZRIP',
                      merchantName: 'DAINASOUL',
                    },
                    transactionInfo: {
                      totalPriceStatus: 'FINAL',
                      totalPriceLabel: 'Total',
                      totalPrice: String((grandTotal / exchangeRate).toFixed(2)),
                      currencyCode: 'USD',
                      countryCode: 'US',
                    },
                  }}
                  onLoadPaymentData={paymentRequest => {
                    console.log('load payment data', paymentRequest);
                    alert("Google Pay Authorized Successfully");
                  }}
                  onError={error => {
                    console.error('Google Pay Error:', error);
                  }}
                />
              </Box> */}
            </Stack>

            <Box sx={{ display: "flex", alignItems: "center", mb: 2, mt: 4 }}>
              <Divider sx={{ flexGrow: 1 }} />
              <Typography
                variant="body2"
                sx={{ px: 2, color: "text.secondary" }}
              >
                OR
              </Typography>
              <Divider sx={{ flexGrow: 1 }} />
            </Box>
          </Box>

          <Box
            sx={{
              // mb: 3,
              backgroundColor: "#fff",
            }}
          >
            {/* Account Section - Accordion */}
            {/* <Accordion
              defaultExpanded
              sx={{
                boxShadow: "none",
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: 0 },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{
                      // Only icon size changes
                      fontSize: "1.2rem", // or '20px' or any size you prefer
                      // No background/border styles here
                    }}
                  />
                }
                sx={{
                  // Container styles remain unchanged
                  "& .MuiAccordionSummary-expandIconWrapper": {
                    // This maintains the clickable area size
                    width: 24, // Default size
                    height: 24,
                    borderRadius: "8px",
                    backgroundColor: "rgb(229 229 229 / 39%)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  },
                  // Other existing styles...
                  minHeight: "auto",
                  padding: "0px 0px",
                  "&.Mui-expanded": { minHeight: "auto" },
                  "& .MuiAccordionSummary-content": {
                    margin: "12px 0",
                    "&.Mui-expanded": { margin: "12px 0" },
                  },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: "14px", color: "#666" }}
                >
                  Account
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pb: 2, mt: -1, padding: "12px 0px" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    // mb: 1,
                  }}
                >
                  <Typography variant="body2">{user.email}</Typography>
                  <Typography
                    variant="body2"
                    onClick={() => logout()}
                    sx={{
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    Log out
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion> */}

            {/* Shipping Address Section - Accordion */}
            {/* <Accordion
                  defaultExpanded
                  sx={{
                    boxShadow: "none",
                    "&:before": { display: "none" },
                    borderTop: "1px solid #e0e0e0",
                    "&.Mui-expanded": { margin: 0 },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon
                        sx={{
                          // Only icon size changes
                          fontSize: "1.2rem", // or '20px' or any size you prefer
                          // No background/border styles here
                        }}
                      />
                    }
                    sx={{
                      // Container styles remain unchanged
                      "& .MuiAccordionSummary-expandIconWrapper": {
                        // This maintains the clickable area size
                        width: 24, // Default size
                        height: 24,
                        borderRadius: "8px",
                        backgroundColor: "rgb(229 229 229 / 39%)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      },
                      // Other existing styles...
                      minHeight: "auto",
                      padding: "0px 0px",
                      "&.Mui-expanded": { minHeight: "auto" },
                      "& .MuiAccordionSummary-content": {
                        margin: "12px 0",
                        "&.Mui-expanded": { margin: "12px 0" },
                      },
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ fontSize: "14px", color: "#666" }}
                    >
                      Ship to
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pb: 2, mt: -1, padding: "12px 0px" }}>
                    <Box
                      sx={{
                        border: "1px solid #e0e0e0",
                        backgroundColor: "rgb(229 229 229 / 39%)",
                        borderRadius: "8px",
                        p: 1.5,
                        mb: 2,
                      }}
                    >
                      <Typography variant="body1" sx={{ fontSize: "14px" }}>
                        {user.name}, Ishha LTD, Ishha Ltd
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", fontSize: "14px" }}
                      >
                        Derby, HA3 7SL, GB
                      </Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      onClick={() =>
                        console.log("Use different address clicked")
                      }
                      sx={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      ＋ Use a different address
                    </Typography>
                  </AccordionDetails>
                </Accordion> */}

            {/* Shipping Method Section - Accordion */}
            {/* <Accordion
                  defaultExpanded
                  sx={{
                    boxShadow: "none",
                    "&:before": { display: "none" },
                    borderTop: "1px solid #e0e0e0",
                    "&.Mui-expanded": { margin: 0 },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon
                        sx={{
                          // Only icon size changes
                          fontSize: "1.2rem", // or '20px' or any size you prefer
                          // No background/border styles here
                        }}
                      />
                    }
                    sx={{
                      // Container styles remain unchanged
                      "& .MuiAccordionSummary-expandIconWrapper": {
                        // This maintains the clickable area size
                        width: 24, // Default size
                        height: 24,
                        borderRadius: "8px",
                        backgroundColor: "rgb(229 229 229 / 39%)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      },
                      // Other existing styles...
                      minHeight: "auto",
                      padding: "0px 0px",
                      "&.Mui-expanded": { minHeight: "auto" },
                      "& .MuiAccordionSummary-content": {
                        margin: "12px 0",
                        "&.Mui-expanded": { margin: "12px 0" },
                      },
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ fontSize: "14px", color: "#666" }}
                    >
                      Shipping method
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pb: 2, mt: -1, padding: "12px 0px" }}>
                    <Box>
                      <Typography variant="body1" sx={{ fontSize: "14px" }}>
                        UK Orders over £125 (FREE) · FREE
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontSize: "14px", color: "#666" }}
                      >
                        Express Delivery
                      </Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion> */}
          </Box>
          {/* <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={
              <Checkbox
                checked={saveInfo}
                onChange={(e) => setSaveInfo(e.target.checked)}
                size="small"
                sx={{
                  "& .MuiSvgIcon-root": {
                    color: "black",
                  },
                }}
              />
            }
            label={
              <Typography fontSize="14px">
                Yes, please keep me informed by email about news, products and
                offers.
              </Typography>
            }
          />
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mb: 2, display: "block", fontSize: "10px" }}
          >
            We may use your email address to contact you to provide assistance
            in completing your order, you can unsubscribe from these emails at
            any time.
          </Typography> */}

          {user ? (
            <>
              <Accordion
                defaultExpanded
                sx={{
                  boxShadow: "none",
                  "&:before": { display: "none" },
                  "&.Mui-expanded": { margin: 0 },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{
                        // Only icon size changes
                        fontSize: "1.2rem", // or '20px' or any size you prefer
                        // No background/border styles here
                      }}
                    />
                  }
                  sx={{
                    // Container styles remain unchanged
                    "& .MuiAccordionSummary-expandIconWrapper": {
                      // This maintains the clickable area size
                      width: 24, // Default size
                      height: 24,
                      borderRadius: "8px",
                      backgroundColor: "rgb(229 229 229 / 39%)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    },
                    // Other existing styles...
                    minHeight: "auto",
                    padding: "0px 0px",
                    "&.Mui-expanded": { minHeight: "auto" },
                    "& .MuiAccordionSummary-content": {
                      margin: "12px 0",
                      "&.Mui-expanded": { margin: "12px 0" },
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontSize: "14px", color: "#666" }}
                  >
                    Account
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pb: 2, mt: -1, padding: "12px 0px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      // mb: 1,
                    }}
                  >
                    <Typography variant="body2">{user.email}</Typography>
                    <Typography
                      variant="body2"
                      onClick={() => logout()}
                      sx={{
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                    >
                      Log out
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </>
          ) : (
            <Box
              sx={{
                borderRadius: "8px",
                mb: 3,
                backgroundColor: "#fff",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Contact
                </Typography>
                <Typography
                  variant="body2"
                  onClick={() => navigate("/login")}
                  sx={{
                    cursor: "pointer",
                    color: "#666",
                    textDecoration: "underline",
                    "&:hover": { color: "#000" },
                  }}
                >
                  Log in
                </Typography>
              </Box>

              <TextField
                fullWidth
                name="email"
                label="Email"
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "&:hover fieldset": {
                      borderColor: "black", // ✅ Hover black border
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "black", // ✅ Focus black border
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "black", // ✅ Default black
                    "&.Mui-focused": {
                      color: "black", // ✅ Focus black
                    },
                  },
                  mt: 1,
                }}
                value={shippingDetails.email || ""}
                onChange={handleShippingChange}
                required
                margin="normal"
                error={Boolean(shippingErrors?.email)}
                helperText={shippingErrors?.email}
              />
            </Box>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={saveInfo}
                onChange={(e) => setSaveInfo(e.target.checked)}
                size="small"
                sx={{
                  "& .MuiSvgIcon-root": {
                    color: "black",
                  },
                }}
              />
            }
            label={
              <Typography fontSize="14px">
                Yes, please keep me informed by email about news, products and
                offers.
              </Typography>
            }
            sx={{ mt: 2 }}
          />
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mb: 2, display: "block", fontSize: "10px" }}
          >
            We may use your email address to contact you to provide assistance
            in completing your order, you can unsubscribe from these emails at
            any time.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: "bold", mt: 4, mb: 1 }}>
            Delivery
          </Typography>

          <CustomAddressForm
            shippingDetails={shippingDetails}
            handleShippingChange={handleShippingChange}
            shippingErrors={shippingErrors}
            isCheckout
          />

          <CheckoutPaymentSection
            paymentMethod={paymentMethod}
            handlePaymentChange={handlePaymentChange}
            useBillingAddress={useBillingAddress}
            handleBillingChange={handleBillingChange}
            cardDetails={cardDetails}
            handleCardDetailsChange={handleCardDetailsChange}
            customerCountry="India"
            shippingDetails={shippingDetails}
            handleShippingChange={handleShippingChange}
          />

          <Box
            sx={{
              border: "1px solid #ddd",
              borderRadius: "10px",
              p: 2,
              mt: 4,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => {
                    setTermsAccepted(e.target.checked);
                    setShowTermsError(!e.target.checked);
                  }}
                  sx={{
                    "& .MuiSvgIcon-root": {
                      color: termsAccepted ? "black" : "red",
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: "14px" }}>
                  I have read and accept the{" "}
                  <span
                    onClick={() => navigate("/terms")}
                    style={{
                      textDecoration: "underline",
                      cursor: "pointer",
                      color: "#000",
                    }}
                  >
                    Terms and Conditions of Sale
                  </span>{" "}
                  and understand the{" "}
                  <span
                    onClick={() => navigate("/privacy")}
                    style={{
                      textDecoration: "underline",
                      cursor: "pointer",
                      color: "#000",
                    }}
                  >
                    Privacy Policy
                  </span>
                  .
                </Typography>
              }
            />

            {showTermsError && (
              <Typography sx={{ color: "red", fontSize: "14px", mt: 1 }}>
                Please indicate that you accept the Terms and Conditions of Sale
                and the Privacy Policy
              </Typography>
            )}
          </Box>

          {paymentMethod === "Paypal" ? (
            <Box sx={{ mt: 3, width: "100%" }}>
              {isReviewLoading ? (
                <Button
                  variant="contained"
                  fullWidth
                  disableRipple
                  disableElevation
                  disabled
                  sx={{
                    backgroundColor: "#000",
                    color: "#fff",
                    fontWeight: "bold",
                    py: 1.5,
                    fontSize: "16px",
                    textTransform: "none",
                    borderRadius: "10px",
                    minHeight: "48px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    "&:hover": { backgroundColor: "#000" },
                    "&.Mui-disabled": {
                      backgroundColor: "#000",
                      color: "#fff",
                      opacity: 1,
                    },
                  }}
                >
                  <CircularProgress
                    size={18}
                    sx={{ color: "white", position: "absolute" }}
                  />
                  <span style={{ visibility: "hidden" }}>Loading</span>
                </Button>
              ) : (
                <Box
                  sx={{
                    borderRadius: "10px",
                    overflow: "hidden",
                    height: "auto",
                    minHeight: "64px",
                  }}
                >
                  <Box
                    sx={{
                      iframe: {
                        borderRadius: "10px !important",
                        height: "52px !important", // ensure iframe respects the height
                      },
                    }}
                  >
                    <PayPalButtons
                      style={{
                        layout: "horizontal",
                        color: "black",
                        shape: "rect",
                        height: 52, // increased from 48 to 52
                        tagline: false,
                      }}
                      createOrder={(data, actions) => {
                        return actions.order.create({
                          purchase_units: [
                            {
                              amount: {
                                value: grandTotal.toFixed(2), // Replace with dynamic value if needed
                              },
                            },
                          ],
                        });
                      }}
                      onApprove={(data, actions) => {
                        return actions.order.capture().then((details) => {
                          alert(
                            `Transaction completed by ${details.payer.name.given_name}`
                          );
                        });
                      }}
                      onError={(err) => {
                        console.error("PayPal Checkout Error:", err);
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disableRipple
              disableElevation
              disabled={isReviewLoading}
              sx={{
                backgroundColor: "#000",
                color: "#fff",
                fontWeight: "bold",
                mt: 3,
                py: 1.5,
                fontSize: "16px",
                textTransform: "none",
                borderRadius: "10px",
                minHeight: "48px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                "&:hover": {
                  backgroundColor: "#000",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#000",
                  color: "#fff",
                  opacity: 1,
                },
              }}
              onClick={handleReviewOrder}
            >
              {isReviewLoading ? (
                <CircularProgress
                  size={18}
                  sx={{ color: "white", position: "absolute" }}
                />
              ) : null}
              <span
                style={{ visibility: isReviewLoading ? "hidden" : "visible" }}
              >
                Review order
              </span>
            </Button>
          )}
        </Box>
      )}

      {/* Right Side - Order Summary (Fixed Desktop Sidebar) */}
      {isDesktop && (
        <Box
          sx={{
            width: { xs: "100%", lg: 500 },
            borderLeft: { lg: "1px solid #e1e1e1" },
            position: "sticky",
            top: 75,
            alignSelf: "flex-start",
            height: "calc(100vh - 75px)",
            overflowY: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            p: 5,
          }}
        >
          <OrderSummaryContent
            cartItems={cartItems}
            formatPrice={formatPrice}
            userCountry={{ code: activeCountryCode }}
            couponInput={couponInput}
            setCouponInput={setCouponInput}
            couponError={couponError}
            setCouponError={setCouponError}
            handleApplyCoupon={handleApplyCoupon}
            isApplyingCoupon={isApplyingCoupon}
            subtotal={subtotal}
            shippingCost={shippingCost}
            appliedCoupon={appliedCoupon}
            discountAmount={discountAmount}
            grandTotal={grandTotal}
            isDesktop={true}
          />
        </Box>
      )}
    </Box>
  );
};

export default CheckoutForm;
